/**
 * The one form-side seam for the blank-value rule (TASK-VTI-TEXT-FIELD CHG-001;
 * SPEC-FRONTEND-RULE-097 (2) and (3); SPEC-SERVICE-INTEGRATION RULE-INTEG-031;
 * D-378).
 *
 * Every client surface that must say "this value is only invisible characters"
 * asks THIS hook: the `VisibleTextField` wrapper, the `ExpandableTextField`
 * `visibleTextCheck` variant, and any form whose control cannot be either of
 * those. There is therefore exactly one client decision and one message, which
 * is the whole point of RULE-097 (1) - a second `.trim()` or a `\p{...}` regex
 * somewhere in a form would consult the BROWSER's Unicode data and could
 * promise a save the gateway then refuses.
 *
 * Three promises this hook keeps, inherited from `web/src/utils/visibleText.ts`:
 *
 * - **The empty string is never blank.** `useVisibleTextError("")` returns
 *   `blank: false`, so a field whose empty value means inherit, clear or absent
 *   keeps that meaning (RULE-INTEG-031 (d)).
 * - **It never rewrites the value.** There is no setter and no trimmed copy in
 *   the return value; what the user typed is what the form sends (RULE-097 (3)).
 * - **It never echoes or logs the value.** The message names neither the value
 *   nor any internal key, and this module writes nothing to console.
 *
 * ## Why `blank` and `helperText` are two separate answers
 *
 * RULE-097 (2) wants the save disabled while a field is blank, from the first
 * render, including a blank value the server already stored. RULE-043 wants
 * inline validation to appear on blur or on submit and NEVER while the user is
 * still typing into a clean field. Both hold at once because they are answers
 * to different questions:
 *
 * - `blank` is the STATE. It is true for a non-empty blank value whatever the
 *   touched state, so a form can keep its save disabled from render one.
 * - `helperText` is the MESSAGE. It is non-null only once the field was blurred,
 *   a submit was attempted, or the value arrived from the server already blank.
 *
 * @module components/shared/VisibleTextField/useVisibleTextError
 */
import { useLingui } from "@lingui/react";

import { isBlankValue } from "../../../utils/visibleText";

import { blankValueMessage } from "./messages";

/** When the shared message may be shown. See the module TSDoc. */
export interface UseVisibleTextErrorOptions {
  /**
   * The field has been blurred at least once. RULE-043's "on blur" leg: until
   * this (or one of the two below) is true the user is still typing and meets
   * no message.
   */
  touched?: boolean;
  /**
   * The surrounding form attempted a submit. RULE-043's "on submit" leg: a
   * submit reveals the message on every blank field at once, even ones the user
   * never focused.
   */
  submitAttempted?: boolean;
  /**
   * The value came from the server already blank. RULE-097 (2)'s last sentence:
   * a stored blank value is flagged on load, because the user did not type it
   * and would otherwise have to guess why the save is disabled.
   */
  initialFromServer?: boolean;
}

/** What a field needs in order to render the rule. */
export interface VisibleTextErrorState {
  /**
   * True when the value is non-empty and every code point in it is invisible.
   * Independent of the touched state, so it is safe as a save-disabled input
   * from the first render.
   */
  blank: boolean;
  /**
   * The ONE shared message, already translated into the reader's locale, or
   * `null` when nothing should be shown yet. Never contains the value.
   */
  helperText: string | null;
}

/**
 * True when ANY value in the list is a non-empty blank value.
 *
 * The save-disabled condition for a form with several in-scope fields, so a
 * form does not hand-roll `values.some(v => v.trim() === "")` - which would be
 * a second, wrong blank test (RULE-097 (1)).
 *
 * Pure, synchronous and not a hook: call it anywhere, including inside a
 * `disabled={...}` expression.
 *
 * @param values - the current values of the in-scope fields.
 * @returns true when at least one of them is non-empty and blank.
 */
export function anyBlank(values: readonly string[]): boolean {
  return values.some((value) => isBlankValue(value));
}

/**
 * Decide whether a text value is blank, and whether its field may say so yet.
 *
 * @param value - the field's CURRENT value, exactly as the user typed it. It is
 *   read, never rewritten.
 * @param options - when the message may appear; see
 *   {@link UseVisibleTextErrorOptions}. Omitted entirely, the message never
 *   appears and only `blank` is useful - which is what a caller that only needs
 *   a save-disabled condition wants.
 * @returns `{ blank, helperText }`; see {@link VisibleTextErrorState}.
 *
 * @example
 * ```tsx
 * const [touched, setTouched] = useState(false);
 * const { blank, helperText } = useVisibleTextError(name, { touched, submitAttempted });
 * <TextField value={name} onBlur={() => setTouched(true)} error={Boolean(helperText)}
 *            helperText={helperText ?? undefined} />
 * <SaveButton disabled={blank} />
 * ```
 */
export function useVisibleTextError(
  value: string,
  options: UseVisibleTextErrorOptions = {},
): VisibleTextErrorState {
  const { _ } = useLingui();
  const { touched = false, submitAttempted = false, initialFromServer = false } = options;

  const blank = isBlankValue(value);
  const mayShow = touched || submitAttempted || initialFromServer;

  return {
    blank,
    helperText: blank && mayShow ? _(blankValueMessage) : null,
  };
}

export default useVisibleTextError;
